import { Component, Story } from './DocTypes';

const PlaygroundStory: Story = {
  title: 'Playground',
  description: 'An autocomplete input where you can change the key attributes',
  coreComponent_v3: {
    html: `<tag3-autocomplete{{...knobs}}></tag3-autocomplete>`,
    js: `
var autocomplete = document.querySelector('tag3-autocomplete');
autocomplete.data = [
  { value: 0, label: 'Pear' },
  { value: 1, label: 'Banana' },
  { value: 2, label: 'Apple' },
  { value: 3, label: 'Orange' },
];`,
    knobs: {
      autocompleteId: {
        default: '',
        editor: 'textbox',
      },
      name: {
        default: '',
        editor: 'textbox',
      },
      value: {
        default: '',
        editor: 'textbox',
      },
      inputText: {
        default: '',
        editor: 'textbox',
      },
      placeholder: {
        default: 'Enter something',
        editor: 'textbox',
      },
      textField: {
        default: '',
        editor: 'textbox',
      },
      valueField: {
        default: '',
        editor: 'textbox',
      },
      disabled: {
        default: false,
        editor: 'checkbox',
      },
      inputDisabled: {
        default: false,
        editor: 'checkbox',
      },
      clearButton: {
        default: true,
        editor: 'checkbox',
      },
    },
  },
  reactWrapper_v3: {
    jsx: `<Tag3Autocomplete{{...knobs}}
  data={[
      { value: 0, label: 'Pear' },
      { value: 1, label: 'Banana' },
      { value: 2, label: 'Apple' },
      { value: 3, label: 'Orange' },
  ]}
/>`,
    knobs: {
      autocompleteId: {
        default: '',
        editor: 'textbox',
      },
      name: {
        default: '',
        editor: 'textbox',
      },
      defaultValue: {
        default: '',
        editor: 'textbox',
        qualifierStart: '{',
        qualifierEnd: '}',
      },
      defaultInputText: {
        default: '',
        editor: 'textbox',
      },
      placeholder: {
        default: 'Enter something',
        editor: 'textbox',
      },
      textField: {
        default: '',
        editor: 'textbox',
      },
      valueField: {
        default: '',
        editor: 'textbox',
      },
      disabled: {
        default: false,
        editor: 'checkbox',
      },
      inputDisabled: {
        default: false,
        editor: 'checkbox',
      },
      clearButton: {
        default: true,
        editor: 'checkbox',
      },
      showFullListOnArrowClick: {
        default: false,
        editor: 'checkbox',
      },
    },
  },
};

const StringArrayStory: Story = {
  title: 'String array',
  description: 'A autocomplete bound to a simple string array',
  coreComponent_v3: {
    html: `<tag3-autocomplete></tag3-autocomplete>`,
    js: `
var autocomplete = document.querySelector('tag3-autocomplete');
autocomplete.data = ["Red", "Blue", "Green"];`,
  },
  reactWrapper_v3: {
    jsx: `<Tag3Autocomplete data={["Red", "Blue", "Green"]} />`,
  },
};

const CustomDataSourceStory: Story = {
  title: 'Custom data source',
  description:
    'An autocomplete where the label and value are bound specific properties in the data source.',
  coreComponent_v3: {
    html: `<tag3-autocomplete text-field="color" value-field="colorId"></tag3-autocomplete>`,
    js: `
var autocomplete = document.querySelector('tag3-autocomplete');
autocomplete.data = [
  { color: "Red", colorId: 1 },
  { color: "Blue", colorId: 2 },
  { color: "Green", colorId: 3 },
];`,
  },
  reactWrapper_v3: {
    jsx: `<Tag3Autocomplete
  textField="color"
  valueField="colorId"
  data={[
    { color: "Red", colorId: 1 },
    { color: "Blue", colorId: 2 },
    { color: "Green", colorId: 3 },
  ]}
/>`,
  },
};

const ControlledValueStory: Story = {
  title: 'Controlled value',
  description: 'An autocomplete where the value is controlled by state',
  reactWrapper_v3: {
    parentJSX: `Example = () => {
  const [value, setValue] = React.useState(2);
  return (
    <div>
    <Tag3Autocomplete{{...knobs}}
      value={value}
      onAutocompleteValueChange={e => setValue(e.detail.value)}
      data={[
        { label: "Red", value: 1 },
        { label: "Blue", value: 2 },
        { label: "Green", value: 3 },
      ]}
    ></Tag3Autocomplete>
    <div>
    <Tag3SimpleButton onButtonClick={() => setValue(1)}>Change value to 1</Tag3SimpleButton>
    </div>
    </div>
  )
}
`,
    jsx: '<Example />',
    knobs: {
      clearButton: {
        default: true,
        editor: 'checkbox',
      },
    },
  },
};

const CustomItemsStory: Story = {
  title: 'Custom items',
  description:
    'An autocomplete with custom items. The component should just render the textField for each item but a renderItem function prop allows us to custom render each item',
  coreComponent_v3: {
    html: `<tag3-autocomplete text-field="name" valueField="id">
  <template data-type='item'>
    <div style='display: flex; align-items: center;'>
      <tag-avatar 
        src='{{image}}'
        size='30px'
        placeholder='{{initials}}'>
      </tag-avatar>
      <tag3-text-feature style='margin-left: 10px;'>{{name}}</tag3-text-feature>
    </div>
  </template> 
</tag3-autocomplete>`,
    js: `
  var autocomplete = document.querySelector('tag3-autocomplete');
  autocomplete.data = [
    {
      id: 1,
      image:
        'https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg?auto=compress&cs=tinysrgb&h=750&w=1260',
      name: 'Jordan Norry',
      initials: 'JN'
    },
    {
      id: 2,
      image:
        'https://images.pexels.com/photos/415829/pexels-photo-415829.jpeg?auto=compress&cs=tinysrgb&h=750&w=1260',
      name: 'Sarah Young',
      initials: 'SY'
    },
    {
      id: 3,
      image:
        'https://images.pexels.com/photos/324658/pexels-photo-324658.jpeg?auto=compress&cs=tinysrgb&h=350',
      name: 'Susan Warmly',
      initials: 'SW'
    },
    {
      id: 4,
      image:
        'https://images.pexels.com/photos/91227/pexels-photo-91227.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1260',
      name: 'Derek Been',
      initials: 'DB'
    },
  ];`,
  },
  reactWrapper_v3: {
    jsx: `<Tag3Autocomplete
    textField='name'
    valueField='id'
    renderItem={(item) => (
      <div style={{display: "flex", "alignItems": "center"}}>
        <Tag3Avatar 
          src={item.image}
          size='30px'
          placeholder={item.initials}>
        </Tag3Avatar>
        <Tag3TextFeature style={{marginLeft: "10px"}}>{item.name}</Tag3TextFeature>
      </div>
    )}
    data={[
      {
        id: 1,
        image:
          'https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg?auto=compress&cs=tinysrgb&h=750&w=1260',
        name: 'Jordan Norry',
        initials: 'JN'
      },
      {
        id: 2,
        image:
          'https://images.pexels.com/photos/415829/pexels-photo-415829.jpeg?auto=compress&cs=tinysrgb&h=750&w=1260',
        name: 'Sarah Young',
        initials: 'SY'
      },
      {
        id: 3,
        image:
          'https://images.pexels.com/photos/324658/pexels-photo-324658.jpeg?auto=compress&cs=tinysrgb&h=350',
        name: 'Susan Warmly',
        initials: 'SW'
      },
      {
        id: 4,
        image:
          'https://images.pexels.com/photos/91227/pexels-photo-91227.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1260',
        name: 'Derek Been',
        initials: 'DB'
      },
    ]}/>`,
  },
};

const ItemsForNoCriteriaStory: Story = {
  title: 'Items for no criteria',
  description:
    'When there is no search criteria and the searchbox gains focus, the drop down list will open showing all the items in the data prop. This can be overridden using a `renderItemsForNoCriteria` function prop. This can be useful if we want to inform the user about what criteria to enter with perhaps a graphic illustration.',
  coreComponent_v3: {
    html: `<tag3-autocomplete text-field="name" value-field="id">
  <template data-type='no-criteria'>
    <div style="text-align: center;padding: 10px">
      <tag3-icon icon="Search" size="50"></tag3-icon>
      <tag3-p>Search for your favourite fruit</tag3-p>
    </div>
  </template>
</tag3-autocomplete>`,
    js: `
  var autocomplete = document.querySelector('tag3-autocomplete');
  autocomplete.data = [
    { id: 0, name: 'Pear' },
    { id: 1, name: 'Banana' },
    { id: 2, name: 'Apple' },
    { id: 3, name: 'Orange' },
    { id: 4, name: 'Kiwi' },
    { id: 5, name: 'Strawberry' },
    { id: 6, name: 'Blackberry' },
    { id: 7, name: 'Blueberry' },
    { id: 8, name: 'Cherry' },
    { id: 9, name: 'Grape' },
    { id: 10, name: 'Grapefruit' },
  ];`,
  },
  reactWrapper_v3: {
    jsx: `<Tag3Autocomplete
    textField='name'
    valueField='id'
    renderItemsForNoCriteria={() => (
      <div style={{textAlign: 'center', padding: '10px'}}>
        <Tag3Icon icon="Search" size={50}></Tag3Icon>
        <Tag3P>Search for your favourite fruit</Tag3P>
      </div>
    )}
    data={[
      { id: 0, name: 'Pear' },
      { id: 1, name: 'Banana' },
      { id: 2, name: 'Apple' },
      { id: 3, name: 'Orange' },
      { id: 4, name: 'Kiwi' },
      { id: 5, name: 'Strawberry' },
      { id: 6, name: 'Blackberry' },
      { id: 7, name: 'Blueberry' },
      { id: 8, name: 'Cherry' },
      { id: 9, name: 'Grape' },
      { id: 10, name: 'Grapefruit' },
    ]}/>`,
  },
};

const NoResultsItemStory: Story = {
  title: 'No results item',
  description:
    'When there is a search criteria but there is no match in the list of items the drop down list will open the no results item that has been passed in. This can be useful if we want to inform the user that there are no items matching the search.',
  reactWrapper_v3: {
    jsx: `<Tag3Autocomplete
    textField='name'
    valueField='id'
    placeholder="Enter a fruit that doesn't exist"
    renderNoResultsItem={() => (
      <div style={{textAlign: 'center', padding: '10px'}}>
        <Tag3P>No items found.</Tag3P>
      </div>
    )}
    data={[
      { id: 0, name: 'Pear' },
      { id: 1, name: 'Banana' },
      { id: 2, name: 'Apple' },
      { id: 3, name: 'Orange' },
      { id: 4, name: 'Kiwi' },
      { id: 5, name: 'Strawberry' },
      { id: 6, name: 'Blackberry' },
      { id: 7, name: 'Blueberry' },
      { id: 8, name: 'Cherry' },
      { id: 9, name: 'Grape' },
      { id: 10, name: 'Grapefruit' },
    ]}/>`,
  },
};

const LoadingItemStory: Story = {
  title: 'Loading item',
  description:
    'When the `isLoading` state is set to true, the drop down list display a loading item instead of the items. By default a TagProgressIndeterminate loading animation will be shown. This can be overridden using a `renderLoadingItem` function prop. This can be useful if we want to inform the user that the tag-autocomplete is waiting for new data (e.g., from an API call).',
  reactWrapper_v3: {
    jsx: `<Tag3Autocomplete
    textField='name'
    valueField='id'
    renderLoadingItem={() => (
      <div style={{height: '150px', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
        <Tag3ProgressIndeterminate text="Waiting for data..." />
      </div>
    )}
    isLoading={true}
    data={[]}/>`,
  },
};

const HeadingStory: Story = {
  title: 'Heading',
  description: 'An autocomplete with custom heading',
  reactWrapper_v3: {
    jsx: `<Tag3Autocomplete 
  textField="name" 
  valueField="id"
  clearButton
  data={[
    { id: 0, name: 'Pear' },
    { id: 1, name: 'Banana' },
    { id: 2, name: 'Apple' },
    { id: 3, name: 'Orange' },
    { id: 4, name: 'Kiwi' },
  ]}
  renderHeadingItem={() => (
      <div style={{padding: "10px",textAlign: "center"}}>
          <Tag3P textStyle={{fontWeight: 'bold'}}>
            Available Fruits
          </Tag3P>
      </div>
    )}
>
</Tag3Autocomplete>`,
  },
};

const FooterStory: Story = {
  title: 'Footer',
  description: 'An autocomplete with custom footer',
  coreComponent_v3: {
    html: `<tag3-autocomplete text-field="name" value-field="id">
  <template data-type='footer'>
    <div style="padding: 10px;text-align: center;">
      <tag3-simple-button accent='atoll'>More results</tag3-simple-button>
    </div>
  </template>
</tag3-autocomplete>`,
    js: `
  var autocomplete = document.querySelector('tag3-autocomplete');
  autocomplete.data = [
    { id: 0, name: 'Pear' },
      { id: 1, name: 'Banana' },
      { id: 2, name: 'Apple' },
      { id: 3, name: 'Orange' },
      { id: 4, name: 'Kiwi' },
  ];
  function handleMoreResults() {
    alert("more results!");
  }
  autocomplete.addEventListener("autocompleteOpen", function () {
    var moreButton = document.querySelector('tag3-simple-button');
    moreButton.addEventListener("buttonClick", handleMoreResults);
  });
  autocomplete.addEventListener("autocompleteClose", function () {
    var moreButton = document.querySelector('tag3-simple-button');
    moreButton.removeEventListener("buttonClick", handleMoreResults);
  });
  `,
  },
  reactWrapper_v3: {
    jsx: `<Tag3Autocomplete 
  textField="name" 
  valueField="id"
  clearButton
  data={[
    { id: 0, name: 'Pear' },
    { id: 1, name: 'Banana' },
    { id: 2, name: 'Apple' },
    { id: 3, name: 'Orange' },
    { id: 4, name: 'Kiwi' },
  ]}
  renderFooter={() => (
      <div style={{padding: "10px",textAlign: "center"}}>
        <Tag3SimpleButton accent='atoll' onButtonClick={() => alert("more results!")}>
          More results
        </Tag3SimpleButton>
      </div>
    )}
>
</Tag3Autocomplete>`,
  },
};

const APIStory: Story = {
  title: 'API',
  description: 'A autocomplete bound to a web API',
  coreComponent_v3: {
    html: `<tag3-autocomplete 
  suppress-internal-filter
  value-field="name" 
  text-field="name" 
  placeholder="Enter a StarWars character"
  container-style='{"width": "250px"}'>
</tag3-autocomplete>`,
    js: `function getMatchesFromAPI(criteria) {
  return fetch("https://swapi.dev/api/people/?search=" + criteria)
    .then(function (response) {
      return response.json();
    })
    .then(function (response) {
      return response.results;
    });
}

var autocomplete = document.querySelector("tag3-autocomplete");
autocomplete.addEventListener('autocompleteSearch', function (e) {
  getMatchesFromAPI(e.detail.value).then(function (data) {
    autocomplete.data = data;
  });
});`,
  },
  reactWrapper_v3: {
    parentJSX: `Example = () => {
  const [data, setData] = React.useState([]);

  async function getMatchesFromAPI(criteria) {
    const response = await fetch("https://swapi.dev/api/people/?search=" + criteria);
    const responseData = await response.json();
    return responseData.results;
  }

  async function handleSearch(e) {
    setData( await getMatchesFromAPI(e.detail.value));
  }

  return (
    <Tag3Autocomplete 
      suppressInternalFilter
      valueField="name" 
      textField="name" 
      placeholder="Enter a StarWars character"
      onAutocompleteSearch={handleSearch}
      data={data} 
      containerStyle={{width: "250px"}}
    />
  )
}
`,
    jsx: '<Example />',
  },
};

export const TagAutocompleteDocs: Component = {
  type: 'component',
  name: 'autocomplete',
  description: 'Renders a autocomplete component',
  mobileFriendly: false,
  popular: false,
  preRelease: '',
  props: [
    { name: 'autocompleteId', description: 'The id of the autocomplete input', type: 'string' },
    { name: 'name', description: 'The name of the autocomplete input', type: 'string' },
    { name: 'defaultValue', description: 'The default value to be selected', type: 'any' },
    { name: 'value', description: 'The value to be selected', type: 'any' },
    { name: 'inputText', description: 'The input text', type: 'string' },
    {
      name: 'searchDelay',
      description: 'The delay before thes search happens',
      type: 'number',
      default: '0',
    },
    { name: 'inputText', description: 'The input text', type: 'string' },
    { name: 'placeholder', description: 'The placeholder of the input', type: 'string' },
    {
      name: 'disabled',
      description: 'Whether the autocomplete is disabled or not',
      type: 'boolean',
      default: 'false',
    },
    {
      name: 'inputDisabled',
      description: 'Whether the autocomplete input is disabled or not',
      type: 'boolean',
      default: 'false',
    },
    {
      name: 'data',
      description: 'The data for the items in the autocomplete',
      type: 'any[]',
    },
    {
      name: 'textField',
      description: 'The field in the data object array to use as the item display text',
      type: 'string',
    },
    {
      name: 'valueField',
      description: 'The field in the data object array to use as the item value',
      type: 'string',
    },
    {
      name: 'clearButton',
      description: 'Defines whether to show a clear button',
      type: 'boolean',
      default: 'false',
    },
    {
      name: 'suppressInternalFilter',
      description:
        'Whether the internal filter is suppressed. i.e. you are filtering the data outside of the component and updating the data prop',
      type: 'boolean',
      default: 'false',
    },
    {
      name: 'containerClassName',
      description: 'Defines a CSS class that is placed on the div container element',
      type: 'string',
    },
    {
      name: 'containerStyle',
      description: 'JSON string of styles to apply to the div container element',
      type: 'string',
    },
    {
      name: 'inputStyle',
      description: 'JSON string of styles to apply to the input element',
      type: 'string',
    },
    {
      name: 'renderFooter',
      description:
        'A function to custom render a footer (or anything at the bottom of the popover)',
      type: `() => React.ReactNode`,
    },
    {
      name: 'renderItem',
      description: 'A function to custom render an item in the list',
      type: `(item: {label: string; value: any}) => React.ReactNode`,
    },
    {
      name: 'renderItemsForNoCriteria',
      description:
        'A function to custom render any content in the popover when the search criteria is empty',
      type: `() => React.ReactNode`,
    },
    {
      name: 'renderNoResultsItem',
      description:
        'A function to custom render any content in the popover when there are no items matching the search criteria',
      type: `() => React.ReactNode`,
    },
    {
      name: 'isLoading',
      description: 'Whether the component is in a loading state or not',
      type: `boolean`,
      default: 'false',
    },
    {
      name: 'renderLoadingItem',
      description:
        'A function to custom render any content in the popover when the component is in a loading state (isLoading = true)',
      type: `() => React.ReactNode`,
    },
    {
      name: 'renderHeadingItem',
      description: 'A function to custom render any content on the top of the popover',
      type: `() => React.ReactNode`,
    },
  ],
  reactPropOverrides: [],
  events: [
    {
      name: 'inputValueChange',
      description: 'Raised when the input value is changed',
      parameters: [{ name: 'value', description: 'The input value', type: 'string' }],
    },
    {
      name: 'autocompleteValueChange',
      description: 'Raised when an item is selected',
      parameters: [
        { name: 'item', description: 'The item from data', type: 'any' },
        { name: 'value', description: 'The value', type: 'any' },
        { name: 'label', description: 'The label', type: 'string' },
      ],
    },
    {
      name: 'autocompleteSearch',
      description: 'Raised when data is searched',
      parameters: [{ name: 'value', description: 'The input value', type: 'string' }],
    },
    {
      name: 'clearClicked',
      description: 'Raised when the clear button has been clicked',
      parameters: [],
    },
    {
      name: 'autocompleteOpen',
      description: 'Raised when the dropdown box is opened',
      parameters: [],
    },
    {
      name: 'autocompleteClose',
      description: 'Raised when the dropdown box is close',
      parameters: [],
    },
  ],
  methods: [],
  stories: [
    PlaygroundStory,
    StringArrayStory,
    CustomDataSourceStory,
    ControlledValueStory,
    CustomItemsStory,
    ItemsForNoCriteriaStory,
    NoResultsItemStory,
    LoadingItemStory,
    HeadingStory,
    FooterStory,
    APIStory,
  ],
};
